import type { SchedulerJob } from '@api/scheduler';
import type { EventChip } from '@components/common/eventChips';
import type { OrderStatus } from '@components/OrderDetails/order-status-config';

export const SCHEDULER_ORDER_STATUS_VALUES = ['lead', 'confirmed'] as const;

export const SCHEDULER_TAG_VALUES = ['stairs', 'security_clearance', 'pre_setup', 'wedding'] as const;

export const SCHEDULER_ASSIGNMENT_STATUS_VALUES = ['assigned', 'unassigned'] as const;

export const SCHEDULER_STAFF_TYPE_VALUES = ['specialist', 'ca_full_time', 'ca_part_time', 'in_house'] as const;

export type SchedulerStaffType = (typeof SCHEDULER_STAFF_TYPE_VALUES)[number];

// The crew filter's types. In-house drivers only drive legs, never crew.
export const SCHEDULER_ASSIGNEE_TYPE_VALUES = ['specialist', 'ca_full_time', 'ca_part_time'] as const;

export type SchedulerAssigneeType = (typeof SCHEDULER_ASSIGNEE_TYPE_VALUES)[number];

export interface SchedulerStaff {
  id: number;
  type: SchedulerStaffType | null;
  name: string;
  confirmed: boolean;
  restrictedVenueAccess: boolean;
}

export type SchedulerCourierBooking = 'booked' | 'pending' | 'unconfirmed' | 'cancelled' | 'not_booked';

export interface SchedulerCourier {
  type: 'lalamove';
  reference: string;
  shareLink?: string;
  booking: SchedulerCourierBooking;
  // false when the order gives the driver the host to call rather than the crew.
  callsCrew?: boolean;
}

// An in-house leg waiting for a person of this kind.
export interface SchedulerUnnamedDriver {
  type: 'unnamed';
  staffType: SchedulerDriverStaffType;
}

export type SchedulerDriver = SchedulerStaff | SchedulerCourier | SchedulerUnnamedDriver;

export type SchedulerAssignmentType = NonNullable<(SchedulerStaff | SchedulerCourier)['type']>;

// A leg is one driver trip for an event: the dropoff to the venue, and the
// pickup from it afterwards. Each leg is staffed with its own driver.
export const SCHEDULER_LEGS = ['dropoff', 'pickup'] as const;

export type SchedulerLeg = (typeof SCHEDULER_LEGS)[number];

export const SCHEDULER_DRIVER_CHANNELS = ['specialist', 'in_house', 'lalamove'] as const;

export type SchedulerDriverChannel = (typeof SCHEDULER_DRIVER_CHANNELS)[number];

export type SchedulerDriverStaffType = Exclude<SchedulerDriverChannel, 'lalamove'>;

export interface SchedulerDriverOption extends SchedulerStaff {
  type: SchedulerDriverStaffType;
  eventCount: number;
  lastEventTeardownAt: string | null;
}

export interface SchedulerAssigneeOption extends SchedulerStaff {
  eventCount: number;
  lastEventTeardownAt: string | null;
}

export type SchedulerRowRef = Pick<SchedulerEvent, 'id' | 'job'>;

export interface SchedulerEventItem {
  quantity: number;
  name: string;
  opsNote: string | null;
}

export interface SchedulerEvent {
  id: number;
  rowKey: string;
  job: SchedulerJob;
  eventId: string;
  eventCode: string | null;
  eventStatus: string;
  multiDay: boolean;
  orderId: number | null;
  orderReference: string | null;
  orderStatus: OrderStatus | null;
  pickupAt: string | null;
  servingAt: string | null;
  teardownAt: string | null;
  productLine: string | null;
  format: string;
  lineCount: number;
  wayCount: number;
  tags: string[];
  chips: EventChip[];
  address: string;
  postalCode: string;
  guestCount: number;
  specialInstructions: string | null;
  locationNotes: string | null;
  remarks: string | null;
  assignees: SchedulerStaff[];
  suggestedAssigneeCount: number | null;
  dropoff: SchedulerDriver | null;
  pickup: SchedulerDriver | null;
  equipment: string | null;
  menuItems: SchedulerEventItem[];
  resaleItems: SchedulerEventItem[];
}

export type SchedulerAssignmentPatch = Partial<Pick<SchedulerEvent, 'assignees' | SchedulerLeg>> & {
  // Ops confirmed a crew change under a live Lalamove booking that calls the crew.
  confirmBookedCrewChange?: true;
};

export type SchedulerEntryRef = SchedulerRowRef & Pick<SchedulerEvent, 'rowKey' | 'eventId'>;

export interface SchedulerAssignmentControls {
  rosterDate: string;
  onAssignmentSave: (entry: SchedulerEntryRef, patch: SchedulerAssignmentPatch) => void;
}

// Resolves once saved; rejects when the save failed (the toast is already shown).
export type OnSchedulerRemarksSave = (entry: SchedulerEntryRef, remarks: string) => Promise<void>;

export const EMPTY_SCHEDULER_EVENTS: readonly SchedulerEvent[] = [];

export const isUnassigned = (event: { assignees: readonly SchedulerStaff[] }) => event.assignees.length === 0;
